import { useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useRequest } from 'ahooks';
import {
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  Form,
  Paging,
  Select,
  Table,
  Tag,
  useBreadcrumbRoutes,
  usePaging,
  Wrapper,
} from '@maxtropy/components';
import {
  apiV2AreaGetValveSelectPost,
  apiV2AreaTaskListPost,
  V2AreaTaskListPostRequest,
  V2AreaTaskListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

const { RangePicker } = DatePicker;

interface FilterParams {
  executeRange?: [Dayjs, Dayjs];
  deviceIds?: number[];
  executionStatus?: number;
}

const statusTextMap: Record<number, string> = {
  0: '待执行',
  1: '已执行',
  2: '执行失败',
  3: '未执行',
  7: '不执行',
};

const statusColorMap: Record<number, string> = {
  0: 'warning',
  1: 'success',
  2: 'error',
  3: 'default',
  7: 'info',
};

const statusOptions = Object.entries(statusTextMap).map(([value, label]) => ({
  label,
  value: Number(value),
}));

const DEFAULT_EXECUTE_RANGE: [Dayjs, Dayjs] = [dayjs().startOf('day'), dayjs().endOf('day')];

const ControlTaskListPage: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const routes = [{ name: '控制任务列表' }];
  const [form] = Form.useForm<FilterParams>();
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [searchParams, setSearchParams] = useState<FilterParams>({
    executeRange: DEFAULT_EXECUTE_RANGE,
  });
  const { data: valveSelectData } = useRequest(apiV2AreaGetValveSelectPost);
  const valveOptions = (valveSelectData?.list ?? [])
    .filter(item => item.id !== undefined && item.name)
    .map(item => ({ label: item.name as string, value: item.id as number }));

  const { data: tableData, loading } = useRequest(
    async () => {
      const [startTime, endTime] = searchParams.executeRange ?? [];
      const normalizedStartTime = startTime?.startOf('day').valueOf();
      const normalizedEndTime = endTime?.endOf('day').valueOf();
      const requestParams: V2AreaTaskListPostRequest = {
        timestamp: Date.now(),
        page: pageOffset,
        size: pageSize,
        executionStartTime: normalizedStartTime,
        executionEndTime: normalizedEndTime,
        executionStatus: searchParams.executionStatus,
        deviceIds: searchParams.deviceIds?.length ? searchParams.deviceIds : undefined,
      };
      const res = await apiV2AreaTaskListPost(requestParams);
      const total = Number((res as V2AreaTaskListPostResponse & { total?: number | string }).total);
      setTotalCount(Number.isFinite(total) && total > 0 ? total : res?.list?.length ?? 0);
      return res?.list ?? [];
    },
    {
      refreshDeps: [pageOffset, pageSize, searchParams],
    }
  );
  const taskIdRowSpanMap = useMemo(() => {
    const rowSpanMap = new Map<number, number>();
    const rows = tableData ?? [];
    let startIndex = 0;

    while (startIndex < rows.length) {
      const currentTaskId = rows[startIndex].taskId ?? '--';
      let endIndex = startIndex + 1;
      while (endIndex < rows.length && (rows[endIndex].taskId ?? '--') === currentTaskId) {
        endIndex += 1;
      }

      rowSpanMap.set(startIndex, endIndex - startIndex);
      for (let i = startIndex + 1; i < endIndex; i += 1) {
        rowSpanMap.set(i, 0);
      }
      startIndex = endIndex;
    }

    return rowSpanMap;
  }, [tableData]);

  const columns = [
    {
      title: '任务id',
      dataIndex: 'taskId',
      ellipsis: true,
      onCell: (_: unknown, index?: number) => ({
        rowSpan: taskIdRowSpanMap.get(index ?? -1) ?? 1,
      }),
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '子任务id',
      dataIndex: 'subTaskId',
      ellipsis: true,
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      ellipsis: true,
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '指令',
      dataIndex: 'command',
      ellipsis: true,
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '依赖任务id',
      dataIndex: 'dependentTaskId',
      ellipsis: true,
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '执行时间',
      dataIndex: 'executionTime',
      ellipsis: true,
      render: (value?: string) => {
        const parsed = value ? dayjs(value) : undefined;
        return <EllipsisSpan value={parsed?.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : '--'} />;
      },
    },
    {
      title: '执行状态',
      dataIndex: 'executionStatus',
      ellipsis: true,
      render: (value?: number) => {
        return (
          <Tag border="solid" color={statusColorMap[value ?? 0]}>
            {statusTextMap[value ?? 0]}
          </Tag>
        );
      },
    },
    {
      title: '目标对象',
      dataIndex: 'targetObject',
      ellipsis: true,
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
    {
      title: '失败原因',
      dataIndex: 'failReason',
      ellipsis: true,
      render: (value?: string) => <EllipsisSpan value={value ?? '--'} />,
    },
  ];

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]} className={styles.wrapper}>
      <div className={styles.page}>
        <CustomFilter<FilterParams>
          form={form}
          onFinish={values => {
            setPageOffset(1);
            setSearchParams({
              executeRange: values.executeRange,
              deviceIds: values.deviceIds,
              executionStatus: values.executionStatus,
            });
          }}
          onReset={() => {
            const resetValues: FilterParams = {
              executeRange: DEFAULT_EXECUTE_RANGE,
            };
            form.setFieldsValue(resetValues);
            setPageOffset(1);
            setSearchParams(resetValues);
          }}
        >
          <Form.Item name="executeRange" label="执行时间" initialValue={DEFAULT_EXECUTE_RANGE}>
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="deviceIds" label="设备名称">
            <Select
              mode="multiple"
              showSearch
              allowClear
              optionFilterProp="label"
              options={valveOptions}
              placeholder="请选择设备名称"
            />
          </Form.Item>
          <Form.Item name="executionStatus" label="执行状态">
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              options={statusOptions}
              placeholder="请选择执行状态"
            />
          </Form.Item>
        </CustomFilter>
        <Table
          rowKey={record => `${record.taskId ?? '--'}-${record.subTaskId ?? '--'}`}
          columns={columns}
          dataSource={tableData ?? []}
          loading={loading}
          pagination={false}
        />
        <Paging pagingInfo={pagingInfo} />
      </div>
    </Wrapper>
  );
};

export default ControlTaskListPage;
